import SubmittedMessage from "./SubmittedMessage"

import {
	LOTTERY_EMAIL_BONUS_MULTIPLIER,
	boostLotteryMultiplier,
} from "@/public/components/features/rereservation/RereservationIncentiveBanner/lotteryEmailBonus"
import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/public/gql/__generated__"

import styles from "../RereservationCompletePageV2.module.scss"

// LOTTERY 用メール登録セクションが表示に使うフィールドは、自分の fragment で宣言する
// (fragment collocation). 親 (EmailSection) は自分の fragment 内で
// ...lotteryEmailSection を spread し、unmask 結果をそのまま渡してくる.
// export は Storybook の makeFragmentData 専用。useQuery / getFragmentData の引数として
// 他ファイルから使うのは禁止.
export const lotteryEmailSectionFragment = graphql(/* GraphQL */ `
	fragment lotteryEmailSection on CanceledReservationV2 {
		registeredEmailByRereservationForm
		incentive {
			lotteryMultiplier
		}
	}
`)

// 抽選キャンペーンの問い合わせ窓口 (ポイントキャンペーン).
// 店舗に問い合わせが行かないよう、連絡先としてこのアドレスを案内する.
const POINT_CAMPAIGN_CONTACT_EMAIL = "point-campaign@souhl.co.jp"

type Props = {
	canceledReservation: FragmentType<typeof lotteryEmailSectionFragment>
	email: string
	setEmail: (email: string) => void
	isEmailSubmitted: boolean
	isSubmittingEmail: boolean
	handleEmailSubmit: (e: React.FormEvent) => void
}

// LOTTERY (抽選キャンペーン) 完了画面用メール登録セクション.
// メールアドレスを登録すると当選確率の倍率が LOTTERY_EMAIL_BONUS_MULTIPLIER 倍になることを前面に出し、登録を後押しする.
// 倍率は当選者選定のときにメール登録済み (email_source=rereservation_form) のくじ券へ掛けるので、
// 「適用されました」表示も同じ条件 (registeredEmailByRereservationForm) に従う.
const LotteryEmailSection = ({
	canceledReservation,
	email,
	setEmail,
	isEmailSubmitted,
	isSubmittingEmail,
	handleEmailSubmit,
}: Props) => {
	const data = getFragmentData(lotteryEmailSectionFragment, canceledReservation)

	// EmailSection 側で「LOTTERY かつ倍率あり」の時だけ描画される前提だが、
	// 型上は null があり得るため防御しておく.
	const multiplier = data.incentive?.lotteryMultiplier ?? null
	if (multiplier === null) {
		return null
	}
	const boostedMultiplier = boostLotteryMultiplier(multiplier)

	// 表示する登録アドレス. 送信直後はサーバ再取得前なので入力値を優先し、
	// それ以外 (リロード・再訪) は DB の登録実態に従う.
	// (入力値優先にすることで、別アドレスで再登録した直後に古いアドレスが出続けるのも防ぐ)
	let registeredEmail: string | null
	if (isEmailSubmitted) {
		registeredEmail = email
	} else {
		registeredEmail = data.registeredEmailByRereservationForm
	}

	// 「メール登録で 2 倍」は見出しで言うので、枠の中では繰り返さない.
	// 枠は「結局あなたの当選確率は何倍か」だけを見せる.
	// 枠はメール入力フォームの直上に置き、「倍増するから登録する」の動機と入力欄を隣接させる.
	let bonusBox: JSX.Element
	// 登録実態で出し分ける. 送信直後だけでなく、
	// 登録済みのお客様が完了画面を開き直した時も「適用されました」を出し続ける.
	if (registeredEmail !== null) {
		bonusBox = (
			<div
				className={`${styles.lottery_bonus_box} ${styles.lottery_bonus_box_applied}`}
			>
				<p className={styles.lottery_bonus_label}>✓ 当選確率が倍増しました</p>
				<p className={styles.lottery_bonus_value}>
					<span className={styles.lottery_bonus_number}>
						{boostedMultiplier}
					</span>
					倍
				</p>
				<p className={styles.lottery_bonus_email}>
					登録メールアドレス: {registeredEmail}
				</p>
			</div>
		)
	} else {
		bonusBox = (
			<div className={styles.lottery_bonus_box}>
				<p className={styles.lottery_bonus_label}>当選確率倍増チャンス</p>
				<p className={styles.lottery_bonus_value}>
					<span className={styles.lottery_bonus_number}>{multiplier}</span>倍 →
					<span className={styles.lottery_bonus_number}>
						{boostedMultiplier}
					</span>
					倍
				</p>
			</div>
		)
	}

	// 予約しただけでは応募にならず、キャンセルすると対象外になる.
	// 読み落とすと「応募したつもり」になるため、注記 (11px) ではなく枠で大きく見せ、
	// 登録前・送信後のどちらでも出す.
	// メール登録カードの中に入れるとカードの主題が「応募の注意」になってしまうので、
	// カードの外 (直上) に独立した枠として置く.
	const conditions = (
		<div className={styles.lottery_conditions}>
			<p className={styles.lottery_conditions_label}>抽選の応募条件</p>
			<div className={styles.lottery_condition}>
				<span
					className={`${styles.lottery_condition_mark} ${styles.lottery_condition_mark_ok}`}
					aria-hidden="true"
				>
					◯
				</span>
				<p>
					<strong className={styles.lottery_condition_visit}>ご来店</strong>
					いただいて
					<br />
					応募完了とさせていただきます
				</p>
			</div>
			<div
				className={`${styles.lottery_condition} ${styles.lottery_condition_ng}`}
			>
				<span
					className={`${styles.lottery_condition_mark} ${styles.lottery_condition_mark_ng}`}
					aria-hidden="true"
				>
					✕
				</span>
				<p>
					ご予約を
					<strong className={styles.lottery_condition_cancel}>
						キャンセル
					</strong>
					すると
					<br />
					キャンペーンの
					<strong className={styles.lottery_condition_cancel}>対象外</strong>
					です
				</p>
			</div>
			<p className={styles.lottery_conditions_note}>
				※ ご予約だけでは抽選の対象になりません
			</p>
		</div>
	)

	let footer: JSX.Element
	if (isEmailSubmitted) {
		footer = <SubmittedMessage />
	} else {
		footer = (
			<div className={styles.lottery_note}>
				<p>
					※
					ご登録のメールアドレスには予約確定・来店前日のリマインドもお送りします
				</p>
				<p>※ 当選者の発表は賞品の発送をもって代えさせていただきます</p>
			</div>
		)
	}

	// 登録済みのお客様には「これから 2 倍になる」ではなく「もう 2 倍になっている」と伝える.
	let title: string
	if (registeredEmail !== null) {
		title = `メール登録で当選確率 ${LOTTERY_EMAIL_BONUS_MULTIPLIER} 倍を適用中`
	} else {
		title = `メール登録で当選確率がさらに ${LOTTERY_EMAIL_BONUS_MULTIPLIER} 倍`
	}

	let submitLabel: string
	if (isSubmittingEmail) {
		submitLabel = "送信中..."
	} else {
		submitLabel = "登録"
	}

	return (
		<>
			{conditions}
			<div
				className={`${styles.email_reminder_section} ${styles.email_reminder_section_lottery}`}
			>
				<div className={styles.gift_badge_row}>
					<span className={styles.lottery_badge}>ポイントキャンペーン</span>
				</div>
				<h3 className={styles.title_lottery}>{title}</h3>
				{bonusBox}
				<form onSubmit={handleEmailSubmit} className={styles.email_form}>
					<div className={styles.email_input_wrapper}>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="example@email.com"
							className={`${styles.email_input} ${styles.email_input_lottery}`}
							disabled={isSubmittingEmail}
						/>
						<button
							type="submit"
							className={styles.email_submit_button}
							disabled={isSubmittingEmail || !email}
						>
							{submitLabel}
						</button>
					</div>
					{footer}
				</form>
				{/* 連絡先は登録前・登録後のどちらでも出す (footer は送信後に差し替わるため外に置く) */}
				<div className={styles.lottery_contact}>
					<p>お問い合わせ（店舗ではなくこちらへ）</p>
					<p>
						<a href={`mailto:${POINT_CAMPAIGN_CONTACT_EMAIL}`}>
							{POINT_CAMPAIGN_CONTACT_EMAIL}
						</a>
					</p>
				</div>
			</div>
		</>
	)
}

export default LotteryEmailSection
